/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import {error} from '@ion/core/src/logger/logger';
import {getAsset, updateAsset} from '@ion/core/src/services/Connect';
import {
  convertOdometerUnits,
  createServiceLog,
  deleteServiceLog,
  editServiceLog,
  getServiceItem,
  loadMaintenanceDashboard,
  loadScheduleServiceStatus,
  loadServiceLogs,
  loadServiceStatus,
  ODOMETER_PROPERTIES
} from '@ion/core/src/services/Maintenance';
import {
  MaintenanceSchedule
} from '@ion/core/src/types/entities';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {getErrorMessage, translatePromptAction} from '@ion/dls/src/utils/ComponentUtils';
import QueryKey, {createKey} from '@ion/query/src/config/QueryKeys';
import {cacheOnFetch, getStaleTime, removeQueries} from '@ion/query/src/utils/helpers';
import {PageInitializeAction} from '@/types/redux';
import {errorOptions} from '../../components/prompts/constants';
import {getAccountId} from '../helpers';
import {toggleUserPrompt} from './user-prompt';

// name of the reducer
export const NAME                               = 'maintenance';

export const INITIALIZE_MAINTENANCE             = 'INITIALIZE_MAINTENANCE';
export const GET_MAINTENANCE_SCHEDULES_REQUEST  = 'GET_MAINTENANCE_SCHEDULES_REQUEST';
export const GET_MAINTENANCE_SCHEDULES_RESPONSE = 'GET_MAINTENANCE_SCHEDULES_RESPONSE';
export const GET_MAINTENANCE_SCHEDULES_ERROR    = 'GET_MAINTENANCE_SCHEDULES_ERROR';

// grid column state
export const SET_MAINTENANCE_GRID_COLUMNS       = 'SET_MAINTENANCE_GRID_COLUMNS';
export const UPDATE_MAINTENANCE_GRID_COLUMNS    = 'UPDATE_MAINTENANCE_GRID_COLUMNS';
export const SET_SCHEDULE_GRID_COLUMNS          = 'SET_SCHEDULE_GRID_COLUMNS';
export const UPDATE_SCHEDULE_GRID_COLUMNS       = 'UPDATE_SCHEDULE_GRID_COLUMNS';
export const SET_HISTORY_GRID_COLUMNS           = 'SET_HISTORY_GRID_COLUMNS';
export const UPDATE_HISTORY_GRID_COLUMNS        = 'UPDATE_HISTORY_GRID_COLUMNS';

export const SET_SELECTED_MAINTENANCE_SCHEDULE  = 'SET_SELECTED_MAINTENANCE_SCHEDULE';
export const GET_SERVICE_STATUS_REQUEST         = 'GET_SERVICE_STATUS_REQUEST';
export const GET_SERVICE_STATUS_RESPONSE        = 'GET_SERVICE_STATUS_RESPONSE';
export const GET_SERVICE_STATUS_ERROR           = 'GET_SERVICE_STATUS_ERROR';
export const GET_SERVICE_LOGS_REQUEST           = 'GET_SERVICE_LOGS_REQUEST';
export const GET_SERVICE_LOGS_RESPONSE          = 'GET_SERVICE_LOGS_RESPONSE';
export const GET_SERVICE_LOGS_ERROR             = 'GET_SERVICE_LOGS_ERROR';
export const VIEW_SERVICE_STATUS                = 'VIEW_SERVICE_STATUS';
export const VIEW_SERVICE_STATUS_REQUEST        = 'VIEW_SERVICE_STATUS_REQUEST';
export const VIEW_SERVICE_STATUS_RESPONSE       = 'VIEW_SERVICE_STATUS_RESPONSE';
export const VIEW_SERVICE_STATUS_ERROR          = 'VIEW_SERVICE_STATUS_ERROR';
export const VIEW_SERVICE_HISTORY               = 'VIEW_SERVICE_HISTORY';
export const VIEW_SERVICE_HISTORY_EDITOR        = 'VIEW_SERVICE_HISTORY_EDITOR';
export const VIEW_SERVICE_HISTORY_REQUEST       = 'VIEW_SERVICE_HISTORY_REQUEST';
export const VIEW_SERVICE_HISTORY_RESPONSE      = 'VIEW_SERVICE_HISTORY_RESPONSE';
export const VIEW_SERVICE_HISTORY_ERROR         = 'VIEW_SERVICE_HISTORY_ERROR';
export const ADD_SERVICE_LOG                    = 'ADD_SERVICE_LOG';
export const ADD_SERVICE_LOG_REQUEST            = 'ADD_SERVICE_LOG_REQUEST';
export const ADD_SERVICE_LOG_RESPONSE           = 'ADD_SERVICE_LOG_RESPONSE';
export const ADD_SERVICE_LOG_ERROR              = 'ADD_SERVICE_LOG_ERROR';
export const EDIT_SERVICE_LOG                   = 'EDIT_SERVICE_LOG';
export const EDIT_SERVICE_LOG_REQUEST           = 'EDIT_SERVICE_LOG_REQUEST';
export const EDIT_SERVICE_LOG_RESPONSE          = 'EDIT_SERVICE_LOG_RESPONSE';
export const EDIT_SERVICE_LOG_ERROR             = 'EDIT_SERVICE_LOG_ERROR';
export const UPDATE_ASSET_REFERENCE_REQUEST     = 'UPDATE_ASSET_REFERENCE_REQUEST';
export const UPDATE_ASSET_REFERENCE_RESPONSE    = 'UPDATE_ASSET_REFERENCE_RESPONSE';
export const UPDATE_ASSET_REFERENCE_ERROR       = 'UPDATE_ASSET_REFERENCE_ERROR';
export const GET_SCHEDULES_SERVICE_STATUS_REQUEST  = 'GET_SCHEDULES_SERVICE_STATUS_REQUEST';
export const GET_SCHEDULES_SERVICE_STATUS_RESPONSE = 'GET_SCHEDULES_SERVICE_STATUS_RESPONSE';
export const GET_SCHEDULES_SERVICE_STATUS_ERROR    = 'GET_SCHEDULES_SERVICE_STATUS_ERROR';
export const DELETE_HISTORY                        = 'DELETE_HISTORY';
export const DELETE_HISTORY_ERROR                  = 'DELETE_HISTORY_ERROR';
export const DELETE_HISTORY_REQUEST                = 'DELETE_HISTORY_REQUEST';
export const DELETE_HISTORY_RESPONSE               = 'DELETE_HISTORY_RESPONSE';

// maintenance dashboard filter model ...
export const SET_MAINTENANCE_DASHBOARD_GRID_FILTER_MODEL = 'SET_MAINTENANCE_DASHBOARD_GRID_FILTER_MODEL';
export const SET_MAINTENANCE_SCHEDULE_GRID_FILTER_MODEL  = 'SET_MAINTENANCE_SCHEDULE_GRID_FILTER_MODEL';
export const SET_MAINTENANCE_HISTORY_GRID_FILTER_MODEL   = 'SET_MAINTENANCE_HISTORY_GRID_FILTER_MODEL';
export const MAINTENANCE_HISTORY_GRID_REFRESHED          = 'MAINTENANCE_HISTORY_GRID_REFRESHED';

const userPromptErrorOptions = {
  ...errorOptions,
  message  : 'common:errors.genericLoading',
  onAction : function() {
    window.location.reload();
  }
};

/**
 * @initialize
 */
export const initializeMaintenance = (): PageInitializeAction => {
  return {type: INITIALIZE_MAINTENANCE};
};

// action to set the active maintenance-schedule grid column state into redux and user preference.
export const setScheduleGridColumns = (columnState: ColumnState[]) => {
  return (dispatch, getState) => {
    const {maintenance: {scheduleGrid}} = getState();
    const columnsState                  = {
      preferedColumnState : scheduleGrid?.preferedColumnState,
      nextColumnState     : columnState
    };
    dispatch({type: SET_SCHEDULE_GRID_COLUMNS, columnsState});
  };
};

// action to update the active maintenance-schedule grid column state user preference.
export const updateScheduleGridColumns = () => {
  return async(dispatch, getState) => {
    const {maintenance: {scheduleGrid}}  = getState();
    const columnState                    = scheduleGrid?.columnState;
    await dispatch({type: UPDATE_SCHEDULE_GRID_COLUMNS, columnState});
  };
};

// action to set the active maintenance-history grid column state into redux and user preference.
export const setHistoryGridColumns = (columnState: ColumnState[]) => {
  return (dispatch, getState) => {
    const {maintenance: {historyGrid}} = getState();
    const columnsState                 = {
      preferedColumnState : historyGrid?.preferedColumnState,
      nextColumnState     : columnState
    };
    dispatch({type: SET_HISTORY_GRID_COLUMNS, columnsState});
  };
};

// action to update the active maintenance-history grid column state into user preference.
export const updateHistoryGridColumns = () => {
  return async(dispatch, getState) => {
    const {maintenance: {historyGrid}}  = getState();
    const columnState                   = historyGrid?.columnState;
    await dispatch({type: UPDATE_HISTORY_GRID_COLUMNS, columnState});
  };
};

export const maintenanceHistoryGridRefreshed = (): PageInitializeAction => {
  return {type: MAINTENANCE_HISTORY_GRID_REFRESHED};
};

/**
 * @getMaintenanceDashboard
 */
export const getMaintenanceDashboard = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  hierarchy,
  callback: callbackFn
}) => {
  return async(dispatch, getState) => {
    dispatch({type: GET_MAINTENANCE_SCHEDULES_REQUEST});
    const data: {
      accountId: number;
      pageId: number;
      pageSize: number;
      sortOn?: string;
      filterOn?: string,
      hierarchy?: boolean
      convertUnits?: boolean
    } = {
      accountId    : getAccountId(getState),
      pageId,
      pageSize,
      hierarchy,
      convertUnits : true
    };
    if (sort) {
      const [sortQuery] = sort;
      data.sortOn = sortQuery;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const response = await loadMaintenanceDashboard(data);
      dispatch({type: GET_MAINTENANCE_SCHEDULES_RESPONSE});
      callbackFn?.(response);
    } catch (err) {
      callbackFn?.({error: true, message: err});
      error(err);
      Promise.all([
        dispatch({type: GET_MAINTENANCE_SCHEDULES_ERROR, err}),
        dispatch(toggleUserPrompt({
          ...userPromptErrorOptions
        }))
      ]);
    }
  };
};

/**
 * @getScheduleServiceStatus
 */
export const getScheduleServiceStatus = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  hierarchy
}) => {
  return async(dispatch, getState) => {
    dispatch({type: GET_SCHEDULES_SERVICE_STATUS_REQUEST});
    const data: {
      accountId: number;
      pageId: number;
      pageSize: number;
      sortOn?: string;
      filterOn?: string,
      hierarchy?: boolean
    } = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      hierarchy
    };
    if (sort) {
      const [sortQuery] = sort;
      data.sortOn = sortQuery;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const response = await loadScheduleServiceStatus(data);
      dispatch({type: GET_SCHEDULES_SERVICE_STATUS_RESPONSE, result: response});
    } catch (err) {
      error(err);
      Promise.all([
        dispatch({type: GET_SCHEDULES_SERVICE_STATUS_ERROR, err}),
        dispatch(toggleUserPrompt({
          ...userPromptErrorOptions
        }))
      ]);
    }
  };
};

/**
 * @refreshSelectedSchedule
 *
 * Re-fetches the currently selected asset's dashboard row and replaces the selected
 * schedule in state. The maintenance dashboard endpoint only supports filtering by
 * `assetName`, so the response is verified against the selected `assetId` before it
 * is committed to state.
 */
export const refreshSelectedSchedule = () => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const {maintenance: {selected}} = getState();
    const asset     = selected?.assetSchedule;
    const assetId   = asset?.assetId;
    const accountId = asset?.accountId;
    const assetName = asset?.name || asset?.assetName;
    if (!assetId || !accountId || !assetName) {
      return;
    }
    try {
      const {items} = await loadMaintenanceDashboard({
        accountId,
        pageId       : 1,
        pageSize     : 20,
        filterOn     : {assetName},
        hierarchy    : true,
        convertUnits : true
      });
      const match = items?.find(row => row?.asset?.assetId === assetId);
      // only commit when the asset is actually found; a miss must not empty the
      // selection, which would bounce the user back to the dashboard.
      if (match?.asset) {
        dispatch({type: SET_SELECTED_MAINTENANCE_SCHEDULE, schedule: match.asset});
      }
    } catch (err) {
      error(err);
    }
  };
};

/**
 * @selectSchedule
 *
 * Sets the selected asset schedule. When entering the detail view, cached service
 * status and history queries are dropped so the detail grids load fresh data, and
 * the selected asset is re-fetched so the info panel reflects current values.
 */
export const selectSchedule = (schedule: MaintenanceSchedule) => {
  return (dispatch: Function) => {
    if (schedule) {
      removeQueries(QueryKey.GET_MAINTENANCE_SERVICE, false);
      removeQueries(QueryKey.GET_MAINTENANCE_HISTORY, false);
    }
    dispatch({type: SET_SELECTED_MAINTENANCE_SCHEDULE, schedule});
    if (schedule) {
      dispatch(refreshSelectedSchedule());
    }
  };
};

/**
 * @getServiceStatus
 */
export const getServiceStatus = (callbackFn) => {
  return async(dispatch, getState) => {
    dispatch({type: GET_SERVICE_STATUS_REQUEST});
    const {maintenance: {selected: {assetSchedule}}} = getState();
    if (!assetSchedule) {
      return callbackFn({error: true, message: 'no selected asset schedule'});
    }
    // [ION-APP TODO]: Parsing should be performed at the Service API level, as opposed to
    // within each client that uses the API.
    try {
      const {
        data: {
          count,
          rows
        },
        status
      } = await cacheOnFetch({
        queryKey  : createKey(QueryKey.GET_MAINTENANCE_SERVICE, assetSchedule.assetId),
        staleTime : getStaleTime(5, false, false) as number,
        queryFn   : async() => await loadServiceStatus(assetSchedule.assetId, true)
      }) as any;
      if (status !== 200) {
        throw new Error('something went wrong');
      }
      dispatch({type: GET_SERVICE_STATUS_RESPONSE});
      callbackFn?.({items: rows, totalCount: count});
    } catch (err) {
      callbackFn?.({error: true, message: err});
      error(err);
      Promise.all([
        dispatch({type: GET_SERVICE_STATUS_ERROR, err}),
        dispatch(toggleUserPrompt({
          ...userPromptErrorOptions
        }))
      ]);
    }
  };
};

/**
 * @getServiceStatus
 */
export const getServiceLogs = (callbackFn) => {
  return async(dispatch, getState) => {
    dispatch({type: GET_SERVICE_LOGS_REQUEST});
    const {maintenance: {selected: {assetSchedule}}} = getState();
    if (!assetSchedule) {
      return callbackFn({error: true, message: 'no selected asset schedule'});
    }
    try {
      const {
        data: {
          count,
          rows
        },
        status
      } = await cacheOnFetch({
        queryKey  : createKey(QueryKey.GET_MAINTENANCE_HISTORY, assetSchedule.assetId),
        staleTime : getStaleTime(5, false, false) as number,
        queryFn   : async() => await loadServiceLogs(assetSchedule.assetId, true)
      }) as any;
      if (status !== 200) {
        throw new Error('something went wrong');
      }
      dispatch({type: GET_SERVICE_LOGS_RESPONSE});
      callbackFn?.({items: rows, totalCount: count});
    } catch (err) {
      callbackFn?.({error: true, message: err});
      error(err);
      Promise.all([
        dispatch({type: GET_SERVICE_LOGS_ERROR, err}),
        dispatch(toggleUserPrompt({
          ...userPromptErrorOptions
        }))
      ]);
    }
  };
};

/**
 * @viewServiceStatus
 *
 * Invoked when the View Service Status editor is to be displayed or removed.
 */
export const viewServiceStatusEditor = (show: boolean = true, serviceItem: GridResult = null) => ({
  type : VIEW_SERVICE_STATUS, show, serviceItem
});

/**
 * @viewServiceStatus
 *
 * Invoked when a View Service Status item is chosen to be viewed. Fetches the
 * service item's details.
 */
export const viewServiceStatus = (serviceItem: GridResult = null) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: VIEW_SERVICE_STATUS_REQUEST});
    try {
      const result = await getServiceItem(serviceItem.serviceItemId, true);
      dispatch({type: VIEW_SERVICE_STATUS_RESPONSE, result});
    } catch (err) {
      error(err);
      Promise.all([
        dispatch({type: VIEW_SERVICE_STATUS_ERROR, err}),
        dispatch(toggleUserPrompt({
          ...userPromptErrorOptions
        }))
      ]);
    }
  };
};

/**
 * @viewServiceStatus
 *
 * Invoked to send the service Item data to the store state
 */
export const viewServiceHistory = (serviceItem = null) => ({
  type : VIEW_SERVICE_HISTORY, serviceItem
});

/**
 * @viewServiceStatus
 *
 * Invoked when the View Service History editor is to be displayed or removed.
 */
export const viewServiceHistoryEditor = (show = true, serviceItem = null) => ({
  type : VIEW_SERVICE_HISTORY_EDITOR, show, serviceItem
});

/**
 * @addLog
 *
 * Invoked when the Add Service Status editor is to be displayed or removed.
 */
export const addLog = (show = true) => ({
  type : ADD_SERVICE_LOG, show
});

/**
 * @addServiceLog
 *
 * Invoked when user submits form to create a new service log.
 */
export const addServiceLog = (data, isReadOnlyUser = false) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_SERVICE_LOG_REQUEST});
    const {
      updateOdometerReference = false,
      updateEngineHourReference = false,
      ...rest
    } = data;
    try {
      const result = await createServiceLog(rest, true);
      if (updateOdometerReference || updateEngineHourReference) {
        const logInfo = {...data};
        await dispatch(updateAssetReference(logInfo, true));
      }
      removeQueries(QueryKey.GET_MAINTENANCE_HISTORY, false);
      removeQueries(QueryKey.GET_MAINTENANCE_SERVICE, false);
      removeQueries(QueryKey.GET_MAINTENANCE_DATA, false);
      dispatch({type: ADD_SERVICE_LOG_RESPONSE, result});
    } catch (err) {
      error(err);
      Promise.all([
        dispatch({type: ADD_SERVICE_LOG_ERROR, err}),
        dispatch(toggleUserPrompt({
          ...userPromptErrorOptions,
          ...({actionLabel: translatePromptAction('common:retry', isReadOnlyUser)}),
          ...(isReadOnlyUser && {onAction: null}),
          message : getErrorMessage(err)

        }))
      ]);
    }
  };
};

/**
 * @editServiceLog
 *
 * Invoked when the Edit Service Status editor is to be displayed or removed.
 */
export const editLog = (show = true, serviceItem = null) => ({
  type : EDIT_SERVICE_LOG, show, serviceItem
});

export const deleteHistoryData = (show = true, serviceItem = null) => ({
  type : DELETE_HISTORY, show, serviceItem
});

/**
 * @removeDeleteHistoryError
 *
 * Invoked when there is need to remove delete history error state.
 */
export const removeDeleteHistoryError = (error) => ({
  type : DELETE_HISTORY_ERROR, error : error
});

/**
 * Invoked when user deletes schedule history existing service log.
 */
export const removeHistory = ({assetServiceLogId}) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_HISTORY_REQUEST});
    try {
      const result = await deleteServiceLog(assetServiceLogId);
      dispatch({type: DELETE_HISTORY_RESPONSE, result});
      removeQueries(QueryKey.GET_MAINTENANCE_HISTORY, false);
      removeQueries(QueryKey.GET_MAINTENANCE_SERVICE, false);
      removeQueries(QueryKey.GET_MAINTENANCE_DATA, false);
    } catch (err) {
      dispatch({type: DELETE_HISTORY_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @updateServiceLog
 *
 * Invoked when user submits form to edit an existing service log.
 */
export const updateServiceLog = (data, isReadOnlyUser = false) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_SERVICE_LOG_REQUEST});
    const {
      updateOdometerReference = false,
      updateEngineHourReference = false,
      ...rest
    } = data;
    try {
      const result = await editServiceLog(rest, rest.assetServiceLogId, true);
      if (updateOdometerReference || updateEngineHourReference) {
        const logInfo = {...data};
        await dispatch(updateAssetReference(logInfo, true));
      }
      removeQueries(QueryKey.GET_MAINTENANCE_HISTORY, false);
      removeQueries(QueryKey.GET_MAINTENANCE_SERVICE, false);
      removeQueries(QueryKey.GET_MAINTENANCE_DATA, false);
      dispatch({type: EDIT_SERVICE_LOG_RESPONSE, result: result.data});
    } catch (err) {
      error(err);
      Promise.all([
        dispatch({type: EDIT_SERVICE_LOG_ERROR, err}),
        dispatch(toggleUserPrompt({
          ...userPromptErrorOptions,
          ...({actionLabel: translatePromptAction('common:retry', isReadOnlyUser)}),
          ...(isReadOnlyUser && {onAction: null}),
          message : getErrorMessage(err)
        }))
      ]);
    }
  };
};

/**
 * @addServiceLog
 *
 * Invoked when user submits form to create a new service log.
 */
export const updateAssetReference = (logInfo, convertUnits) => {
  return async(dispatch: Function): Promise<any> => {
    if (convertUnits) {
      logInfo = convertOdometerUnits(logInfo, ODOMETER_PROPERTIES.ASSET, {reverse: true});
    }
    dispatch({type: UPDATE_ASSET_REFERENCE_REQUEST});
    try {
      const asset = await getAsset(logInfo.assetId);
      const assetData = {
        ...asset,
        odometerReference :
          logInfo.updateOdometerReference
            ? logInfo.odometer
            : asset.odometerReference,
        odometerReferenceTime :
          logInfo.updateOdometerReference
            ? logInfo.serviceDate
            : asset.odometerReferenceTime,
        engineHoursReference :
          logInfo.updateEngineHourReference
            ? logInfo.engineHours
            : asset.engineHoursReference,
        engineHoursReferenceTime :
          logInfo.updateEngineHourReference
            ? logInfo.serviceDate
            : asset.engineHoursReferenceTime
      };
      await updateAsset(assetData);
      dispatch({type: UPDATE_ASSET_REFERENCE_RESPONSE});
    } catch (err) {
      error(err);
      dispatch({type: UPDATE_ASSET_REFERENCE_ERROR, err});
    }
  };
};

export const setFilterModel = (model: FilterModel) => ({
  type : SET_MAINTENANCE_DASHBOARD_GRID_FILTER_MODEL, model
});

export const setScheduleFilterModel = (model: FilterModel) => ({
  type : SET_MAINTENANCE_SCHEDULE_GRID_FILTER_MODEL, model
});

export const setHistoryFilterModel = (model: FilterModel) => ({
  type : SET_MAINTENANCE_HISTORY_GRID_FILTER_MODEL, model
});
